import type { ReactElement } from 'react';

import type { Toast as ToastModel, ToastTone } from '../notifications/toastHub';
import { Button } from './Button';
import type { Hue } from './hue';
import { IconButton } from './IconButton';
import { Icon } from './Icon';
import type { IconName } from './iconName';
import styles from './Toast.module.css';

/**
 * One toast — docs/DESIGN.md §6.6, ADR-0101 §7.10.
 *
 * SEVERITY IS NEVER CARRIED BY COLOUR ALONE. Three independent channels, always all three:
 *   1. the GLYPH SHAPE — ring+tick, ring+bang, TRIANGLE+bang, ring+i;
 *   2. the WORD — Success / Error / Warning / Note, passed in already localised;
 *   3. the TONE — the disc's fill, the hue edge and the halo.
 * A greyscale screenshot of this component loses nothing. That was a measured defect in the
 * directions this one replaced, and it is why `toneLabel` is a required prop rather than an
 * optional nicety.
 *
 * ADR-0101 §7.10 changes only where channel two is DRAWN. The word used to sit above the
 * sentence as a mono-caps label in `--tx-3`, which is the one ink tier §1 rule 5 forbids on a
 * wash, and it repeated in text what the disc beside it already said in shape and colour. It
 * is now a visually hidden prefix INSIDE the message, so it is still first in reading order
 * and still in `textContent` — the `Toaster.test` tone-word assertions are about exactly that
 * — while the row it used to occupy is gone.
 */

const GLYPH: Record<ToastTone, IconName> = {
  success: 'check',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

/** The hue family of each tone (ADR-0101 §7.10) — the disc, the edge, the halo, the action. */
const HUE: Record<ToastTone, Hue> = {
  success: 'lime',
  error: 'coral',
  warning: 'amber',
  info: 'aqua',
};

export interface ToastProps {
  readonly toast: ToastModel;
  /** Success / Error / Warning / Note — localised by the caller (ADR-0003). */
  readonly toneLabel: string;
  /** Accessible name of the close control, localised by the caller. */
  readonly dismissLabel: string;
  readonly onDismiss: (id: string) => void;
}

export function Toast({ toast, toneLabel, dismissLabel, onDismiss }: ToastProps): ReactElement {
  const { id, tone, message, action } = toast;

  return (
    <div
      className={styles.toast}
      // Explicit border for Windows High Contrast, where the tone step collapses (base.css).
      data-surface="floating"
      // The hue drives every colour; the tone stays because it is the SEVERITY, and what a
      // stylesheet or the e2e suite asks for is "the dangerous one", not "the coral one".
      data-hue={HUE[tone]}
      data-tone={tone}
      // Only an error interrupts. Everything else rides the region's `aria-live="polite"`
      // (docs/DESIGN.md §6.6, §7.5).
      role={tone === 'error' ? 'alert' : undefined}
    >
      <span aria-hidden="true" className={styles.disc}>
        <Icon name={GLYPH[tone]} size={16} />
      </span>

      <div className={styles.body}>
        <p className={styles.message}>
          {/*
            Channel two, read but not drawn. It is a CHILD ELEMENT rather than a text node so
            that the message element's own text is exactly the sentence: Testing Library's
            `getByText` concatenates direct text children only, which is what keeps both
            `getByText('Success')` and `getByText('Formatted 3 lines.')` unambiguous.
          */}
          <b className={styles.toneWord}>{toneLabel}</b>
          {message}
        </p>

        {action === undefined ? null : (
          <div className={styles.actions}>
            <Button
              variant="tinted"
              hue={HUE[tone]}
              size="sm"
              label={action.label}
              {...(action.shortcutHint !== undefined && { shortcutHint: action.shortcutHint })}
              onClick={() => {
                action.onInvoke();
                onDismiss(id);
              }}
            />
          </div>
        )}
      </div>

      <IconButton
        icon="close"
        label={dismissLabel}
        className={styles.close}
        onClick={() => {
          onDismiss(id);
        }}
      />
    </div>
  );
}

export default Toast;
